'use es6';

import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import I18n from 'I18n';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import partial from 'react-utils/partial';
import Controllable from '../decorators/Controllable';
import H4 from '../elements/headings/H4';
import H7 from '../elements/headings/H7';
import { rgba } from '../core/Color';
import Small from '../elements/Small';
import SyntheticEvent from '../core/SyntheticEvent';
import UIButtonWrapper from '../layout/UIButtonWrapper';
import UIClickable from '../button/UIClickable';
import UIImage from '../image/UIImage';
import UINextButton from '../button/UINextButton';
import UIPreviousButton from '../button/UIPreviousButton';
import { BUTTON_RADIUS, BUTTON_SM_WIDTH, GRID_BREAKPOINT_SMALL } from 'HubStyleTokens/sizes';
import { CALYPSO, CALYPSO_MEDIUM } from 'HubStyleTokens/colors';
import styled from 'styled-components';
import { toPx } from '../utils/Styles';
import { uniqueId } from '../utils/underscore';
import { getComponentPropType } from '../utils/propTypes/componentProp';

var preloadImages = function preloadImages(steps) {
  steps.forEach(function (step) {
    if (step.image) {
      var img = new Image();
      img.src = step.image;
    }
  });
};

var DEFAULT_MAX_WIDTH = 826; // width + gutters

var GUTTER_SIZE = 20;
var MAX_THUMBNAILS = 5;
var THUMBNAIL_ASPECT_RATIO = 0.6909090909;
/* based on 76/110 spec */

var THUMBNAIL_GAP = 24;
var FLEX_FALLBACK_SPACE = parseInt(BUTTON_SM_WIDTH, 10) + GUTTER_SIZE;
var STACKED_LAYOUT = "\n  'header header    header  '\n  '.      steps     .       '\n  'prev   contents  next    '\n  '.      callout   .       '\n  '.      footer    .       '\n";
var RESPONSIVE_LAYOUT = "\n  'header     header      header  '\n  'steps      steps       steps   '\n  'contents   contents    contents'\n  'prev       footer      next    '\n  'callout    callout     callout '\n";
var CarouselWrapper = styled.section.withConfig({
  displayName: "UICarousel__CarouselWrapper",
  componentId: "mz6b1f-0"
})(["display:flex;flex-wrap:wrap;grid-column-gap:", ";grid-template-areas:", ";grid-template-columns:auto 1fr auto;max-width:", ";padding:", ";padding-top:0;position:relative;> *{flex:1 1 100%;}@supports (display:grid){display:grid;}@media only screen and (max-width:", "){grid-template-areas:", ";grid-template-columns:auto 1fr auto;&& > *{margin-right:0;}}"], toPx(GUTTER_SIZE), STACKED_LAYOUT, function (props) {
  return toPx(props.maxWidth);
}, toPx(GUTTER_SIZE), GRID_BREAKPOINT_SMALL, RESPONSIVE_LAYOUT);
var CarouselHeader = styled.div.withConfig({
  displayName: "UICarousel__CarouselHeader",
  componentId: "mz6b1f-1"
})(["grid-area:header;margin-bottom:24px;text-align:center;@supports (display:grid){margin-right:0;}"]);
var CarouselImage = styled(UIImage).withConfig({
  displayName: "UICarousel__CarouselImage",
  componentId: "mz6b1f-2"
})(["margin:0 auto;"]);
var CarouselTitle = styled(H4).withConfig({
  displayName: "UICarousel__CarouselTitle",
  componentId: "mz6b1f-3"
})(["margin-bottom:0;"]);
var CarouselSubtitle = styled(H7).withConfig({
  displayName: "UICarousel__CarouselSubtitle",
  componentId: "mz6b1f-4"
})(["margin-bottom:12px;"]);
var CarouselCallout = styled.aside.withConfig({
  displayName: "UICarousel__CarouselCallout",
  componentId: "mz6b1f-5"
})(["grid-area:callout;margin-top:", ";margin-left:", ";margin-right:", ";text-align:center;@supports (display:grid){margin-left:0;margin-right:0;}@media only screen and (max-width:", "){margin-top:", ";padding-left:0;}"], toPx(GUTTER_SIZE), toPx(FLEX_FALLBACK_SPACE), toPx(FLEX_FALLBACK_SPACE), GRID_BREAKPOINT_SMALL, toPx(GUTTER_SIZE));
var CarouselContent = styled.section.withConfig({
  displayName: "UICarousel__CarouselContent",
  componentId: "mz6b1f-6"
})(["flex-basis:calc(100% - ", ");grid-area:contents;align-self:center;"], toPx(FLEX_FALLBACK_SPACE * 2));
var CarouselContentInner = styled.div.withConfig({
  displayName: "UICarousel__CarouselContentInner",
  componentId: "mz6b1f-7"
})([">:last-child{margin-bottom:0;}"]);
var CarouselNext = styled.div.withConfig({
  displayName: "UICarousel__CarouselNext",
  componentId: "mz6b1f-8"
})(["align-self:center;flex-basis:", ";grid-area:next;text-align:right;"], toPx(FLEX_FALLBACK_SPACE));
var CarouselPrevious = styled.div.withConfig({
  displayName: "UICarousel__CarouselPrevious",
  componentId: "mz6b1f-9"
})(["align-self:center;flex-basis:", ";grid-area:prev;"], toPx(FLEX_FALLBACK_SPACE));
var CarouselFooter = styled.footer.withConfig({
  displayName: "UICarousel__CarouselFooter",
  componentId: "mz6b1f-10"
})(["grid-area:footer;margin-top:", ";padding:0 ", ";@supports (display:grid){margin-right:0;padding:0;}"], toPx(GUTTER_SIZE), toPx(FLEX_FALLBACK_SPACE));
var CarouselCurrent = styled(Small).withConfig({
  displayName: "UICarousel__CarouselCurrent",
  componentId: "mz6b1f-11"
})(["display:block;margin:0 8px;text-align:center;"]);
var CarouselThumbnail = styled(UIClickable).withConfig({
  displayName: "UICarousel__CarouselThumbnail",
  componentId: "mz6b1f-12"
})(["flex:0 1;flex-basis:calc(20% - ", ");border-radius:", ";box-shadow:", ";"], toPx(THUMBNAIL_GAP), BUTTON_RADIUS, function (props) {
  return props['aria-current'] ? "\n        0 0 0 2px " + CALYPSO + ",\n        0 0 12px 0 " + rgba(CALYPSO, 0.5) + "\n      " : "0 0 0 1px " + CALYPSO_MEDIUM;
});
var AspectRatioBox = styled.div.withConfig({
  displayName: "UICarousel__AspectRatioBox",
  componentId: "mz6b1f-13"
})(["height:0;overflow:hidden;padding-top:calc(100% * ", ");position:relative;"], THUMBNAIL_ASPECT_RATIO);
var CarouselThumbnailImage = styled(UIImage).withConfig({
  displayName: "UICarousel__CarouselThumbnailImage",
  componentId: "mz6b1f-14"
})(["height:auto;left:50%;min-height:100%;min-width:100%;object-fit:cover;position:absolute;top:50%;transform:translateX(-50%) translateY(-50%);width:auto;"]);
var USE_OPTIONS = ['max', 'lite'];

var isValidStepIndex = function isValidStepIndex(stepIndex, steps) {
  return !(stepIndex < 0 || stepIndex > steps.length - 1);
};

var getMaxIndex = function getMaxIndex(arr) {
  return arr.length - 1;
};

var UICarousel = /*#__PURE__*/function (_Component) {
  _inherits(UICarousel, _Component);

  function UICarousel() {
    var _getPrototypeOf2;

    var _this;

    _classCallCheck(this, UICarousel);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _possibleConstructorReturn(this, (_getPrototypeOf2 = _getPrototypeOf(UICarousel)).call.apply(_getPrototypeOf2, [this].concat(args)));

    _this.handleClickPreviousStep = function () {
      var _this$props = _this.props,
          allowStepLoop = _this$props.allowStepLoop,
          stepIndex = _this$props.stepIndex,
          steps = _this$props.steps;
      var computedStepIndex = isValidStepIndex(stepIndex, steps) ? stepIndex : 0;
      var newIndex;

      if (allowStepLoop && computedStepIndex === 0) {
        newIndex = getMaxIndex(steps);
      } else {
        newIndex = computedStepIndex - 1;
      }

      if (newIndex >= 0) {
        _this.handleChange(newIndex);
      }
    };

    _this.handleClickNextStep = function () {
      var _this$props2 = _this.props,
          allowStepLoop = _this$props2.allowStepLoop,
          stepIndex = _this$props2.stepIndex,
          steps = _this$props2.steps;
      var computedStepIndex = isValidStepIndex(stepIndex, steps) ? stepIndex : 0;
      var newIndex;

      if (allowStepLoop && getMaxIndex(steps) === computedStepIndex) {
        newIndex = 0;
      } else {
        newIndex = computedStepIndex + 1;
      }

      if (newIndex <= getMaxIndex(steps)) {
        _this.handleChange(newIndex);
      }
    };

    _this.handleChange = function (newIndex) {
      _this.props.onStepIndexChange(SyntheticEvent(newIndex));
    };

    _this.handleKeyDown = function (evt) {
      if (evt.key === 'ArrowRight') {
        _this.handleClickNextStep();
      }

      if (evt.key === 'ArrowLeft') {
        _this.handleClickPreviousStep();
      }
    };

    return _this;
  }

  _createClass(UICarousel, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      var steps = this.props.steps;
      preloadImages(steps);
    }
  }, {
    key: "componentDidUpdate",
    value: function componentDidUpdate() {
      var steps = this.props.steps;
      preloadImages(steps);
    }
  }, {
    key: "renderCount",
    value: function renderCount(steps, computedStepIndex) {
      var totalSteps = steps.length;
      var currentStepDisplayNumber = computedStepIndex + 1;
      return React.createElement(CarouselCurrent, {
        use: "help"
      }, React.createElement("span", {
        className: "sr-only"
      }, I18n.text('ui.UICarousel.current', {
        currentStepDisplayNumber: currentStepDisplayNumber,
        totalSteps: totalSteps
      })), React.createElement("span", {
        "aria-hidden": true
      }, currentStepDisplayNumber + "/" + totalSteps));
    }
  }, {
    key: "renderFooter",
    value: function renderFooter(allowStepLoop, use, steps, computedStepIndex) {
      var totalSteps = steps.length;
      var renderedCount = allowStepLoop ? null : this.renderCount(steps, computedStepIndex);
      var renderedFooter;
      renderedFooter = renderedCount;

      if (use === 'max') {
        renderedFooter = totalSteps > MAX_THUMBNAILS ? renderedCount : this.renderThumbnails(steps, computedStepIndex);
      }

      return renderedFooter && React.createElement(CarouselFooter, {
        use: use
      }, renderedFooter);
    }
  }, {
    key: "renderThumbnails",
    value: function renderThumbnails(steps, computedStepIndex) {
      var _this2 = this;

      var totalThumbs = steps;
      var thumbs = totalThumbs.map(function (step, i) {
        if (!step.image) return null;
        var isCurrentStep = computedStepIndex === i || undefined;
        return React.createElement(CarouselThumbnail, {
          "aria-current": isCurrentStep,
          key: i,
          onClick: partial(_this2.handleChange, i)
        }, React.createElement(AspectRatioBox, null, React.createElement(CarouselThumbnailImage, {
          alt: step.alt,
          src: step.image
        })));
      });
      return React.createElement(UIButtonWrapper, {
        alignItems: "start",
        horizontalGap: THUMBNAIL_GAP
      }, thumbs);
    }
  }, {
    key: "render",
    value: function render() {
      var _this$props3 = this.props,
          allowStepLoop = _this$props3.allowStepLoop,
          Heading = _this$props3.Heading,
          id = _this$props3.id,
          imageHeight = _this$props3.imageHeight,
          MainContent = _this$props3.MainContent,
          maxWidth = _this$props3.maxWidth,
          __onStepIndexChange = _this$props3.onStepIndexChange,
          stepIndex = _this$props3.stepIndex,
          steps = _this$props3.steps,
          titleText = _this$props3.titleText,
          use = _this$props3.use,
          rest = _objectWithoutProperties(_this$props3, ["allowStepLoop", "Heading", "id", "imageHeight", "MainContent", "maxWidth", "onStepIndexChange", "stepIndex", "steps", "titleText", "use"]);

      var computedStepIndex = isValidStepIndex(stepIndex, steps) ? stepIndex : 0;
      this._id = this._id || uniqueId('carousel');
      var computedId = id || this._id;
      var describedById = computedId + "-desc";
      var currentStep = steps[computedStepIndex];
      var isFirstStep = computedStepIndex === 0;
      var isLastStep = computedStepIndex === getMaxIndex(steps);
      return React.createElement(CarouselWrapper, Object.assign({}, rest, {
        "aria-atomic": true,
        "aria-describedby": describedById,
        "aria-live": "polite",
        id: computedId,
        onKeyDown: this.handleKeyDown,
        maxWidth: maxWidth,
        tabIndex: 0,
        use: use
      }), React.createElement(CarouselHeader, {
        use: use
      }, titleText && React.createElement(Heading, null, titleText), React.createElement("div", {
        "aria-hidden": true,
        className: "sr-only",
        id: describedById
      }, I18n.text('ui.UICarousel.tip'))), React.createElement(CarouselPrevious, null, React.createElement(UIPreviousButton, {
        "aria-controls": computedId,
        "aria-hidden": true,
        "aria-label": I18n.text('ui.UICarousel.prev'),
        onClick: this.handleClickPreviousStep,
        disabled: isFirstStep && !allowStepLoop,
        size: "sm"
      })), React.createElement(CarouselContent, {
        use: use
      }, React.createElement(MainContent, null, currentStep.image ? React.createElement(CarouselImage, {
        alt: currentStep.alt,
        objectFit: "contain",
        shape: "rounded",
        src: currentStep.image,
        style: {
          height: imageHeight
        }
      }) : currentStep.content)), React.createElement(CarouselNext, null, React.createElement(UINextButton, {
        "aria-controls": computedId,
        "aria-hidden": true,
        "aria-label": I18n.text('ui.UICarousel.next'),
        onClick: this.handleClickNextStep,
        disabled: isLastStep && !allowStepLoop,
        size: "sm"
      })), currentStep.description && React.createElement(CarouselCallout, {
        use: use
      }, currentStep.name && React.createElement(CarouselSubtitle, {
        role: "presentation"
      }, currentStep.name), currentStep.description), this.renderFooter(allowStepLoop, use, steps, computedStepIndex));
    }
  }]);

  return UICarousel;
}(Component);

UICarousel.propTypes = {
  allowStepLoop: PropTypes.bool.isRequired,
  defaultStepIndex: PropTypes.number,
  Heading: getComponentPropType(H4),
  imageHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  MainContent: PropTypes.elementType,
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onStepIndexChange: PropTypes.func,
  steps: PropTypes.arrayOf(PropTypes.shape({
    alt: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.node,
    content: PropTypes.node,
    image: PropTypes.string
  })),
  stepIndex: PropTypes.number,
  titleText: PropTypes.node,
  use: PropTypes.oneOf(USE_OPTIONS)
};
UICarousel.defaultProps = {
  allowStepLoop: false,
  MainContent: CarouselContentInner,
  maxWidth: DEFAULT_MAX_WIDTH,
  Heading: CarouselTitle,
  stepIndex: 0,
  use: 'lite'
};
UICarousel.displayName = 'UICarousel';
export default Controllable(UICarousel, ['stepIndex']);